import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, LabelList,
} from 'recharts';
import { satisfaction as api } from '../services/api';

/**
 * Tableau de bord Satisfaction.
 *
 * Volontairement calqué sur la grammaire du tableau de bord JDMA — un KPI
 * dominant avec sa variation, des cartes en deux colonnes, des classements
 * courts — pour qu'un utilisateur qui passe de l'un à l'autre n'ait rien à
 * réapprendre. Première version (2026-09-17) : sept cartes de même poids et un
 * tableau de 37 lignes sur 9 colonnes, soit un déversement de données plutôt
 * qu'un tableau de bord.
 *
 * Deux partis pris repris de JDMA :
 *   — la variation vs période précédente, sans quoi on rapporte sans informer ;
 *   — la RÉPARTITION des notes et pas seulement la moyenne : 7,7 peut être
 *     « tout le monde à 8 » ou « des 10 et un paquet de 2 », deux situations
 *     opposées dont une seule demande d'agir ; *
 * ⚠️ Classes `sat-` en propre, jamais les `jd-` de JDMA : réutiliser le CSS
 * d'une autre page l'a déjà cassé deux fois dans la journée.
 */

const nf = new Intl.NumberFormat('fr-FR');
const pct = (a, b) => (b ? Math.round((a / b) * 100) : null);
const un = (v) => (v === null || v === undefined ? '—' : String(v).replace('.', ','));

// Seuils usuels du NPS : au-dessus de 50 c'est excellent, sous zéro les
// détracteurs l'emportent.
const tonScore = (s) => (s === null || s === undefined ? '' : s >= 50 ? 'bon' : s >= 0 ? 'moyen' : 'mauvais');

const MOIS = (code) => {
  const [a, m] = String(code).split('-');
  return `${m}/${a.slice(2)}`;
};

const RACCOURCIS = [
  { cle: '30', label: '30 jours', jours: 30 },
  { cle: '90', label: '3 mois', jours: 90 },
  { cle: '365', label: '12 mois', jours: 365 },
  { cle: '', label: 'Tout', jours: null },
];
const ilYA = (jours) => {
  const d = new Date();
  d.setDate(d.getDate() - jours);
  return d.toISOString().slice(0, 10);
};

/** Variation par rapport à la période précédente. Muette si on n'a pas de quoi comparer. */
function Delta({ valeur, avant, suffixe = '', decimales = 0 }) {
  if (valeur === null || valeur === undefined || avant === null || avant === undefined) return null;
  const d = valeur - avant;
  if (Math.abs(d) < (decimales ? 0.05 : 0.5)) {
    return <span className="sat-delta sat-delta-stable">= stable</span>;
  }
  return (
    <span className={`sat-delta ${d > 0 ? 'sat-delta-haut' : 'sat-delta-bas'}`}>
      {d > 0 ? '▲' : '▼'} {un(Math.abs(d).toFixed(decimales))}{suffixe}
    </span>
  );
}

export default function SatisfactionTableauBord({ formulaires }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [formulaireId, setFormulaireId] = useState('');
  const [raccourci, setRaccourci] = useState('90');
  const [du, setDu] = useState(ilYA(90));
  const [auj, setAuj] = useState('');
  const [toutVoir, setToutVoir] = useState(false);
  const [tri, setTri] = useState({ c: 'reponses', desc: true });

  const charger = useCallback(async () => {
    setErreur('');
    try {
      const params = {};
      if (formulaireId) params.formulaire_id = formulaireId;
      if (du) params.du = du;
      if (auj) params.au = auj;
      const { data: d } = await api.tableauBord(params);
      setData(d);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Chargement impossible');
    }
  }, [formulaireId, du, auj]);

  useEffect(() => { charger(); }, [charger]);

  const choisirRaccourci = (r) => {
    setRaccourci(r.cle);
    setDu(r.jours ? ilYA(r.jours) : '');
    setAuj('');
  };

  const sites = useMemo(() => {
    const l = [...(data?.sites || [])];
    const acces = {
      site_nom: (s) => s.site_nom || '',
      envois: (s) => s.envois, reponses: (s) => s.reponses,
      taux: (s) => pct(s.reponses, s.envois) ?? -1,
      score_nps: (s) => (s.score_nps === null ? -999 : s.score_nps),
      note_moyenne: (s) => s.note_moyenne ?? -1,
      detracteurs: (s) => s.detracteurs, a_traiter: (s) => s.a_traiter,
    };
    const f = acces[tri.c] || acces.reponses;
    l.sort((a, b) => {
      const x = f(a); const y = f(b);
      const c = typeof x === 'number' ? x - y : String(x).localeCompare(String(y), 'fr');
      return tri.desc ? -c : c;
    });
    return l;
  }, [data, tri]);

  const trier = (c) => setTri((t) => (t.c === c ? { c, desc: !t.desc } : { c, desc: c !== 'site_nom' }));
  const fleche = (c) => (tri.c === c ? <span className="sat-fleche">{tri.desc ? '▾' : '▴'}</span> : null);

  const serie = useMemo(
    () => (data?.serie || []).map((m) => ({ ...m, label: MOIS(m.mois) })), [data]);

  // Axe du score resserré autour des valeurs réelles : sur −100 à +100 une
  // courbe qui va de 35 à 42 serait parfaitement plate. Les étiquettes portent
  // la valeur exacte au-dessus de chaque point, donc l'œil lit la forme sans
  // que l'échelle puisse tromper.
  const domaineScore = useMemo(() => {
    const v = serie.map((m) => m.score_nps).filter((x) => x !== null && x !== undefined);
    if (!v.length) return [0, 100];
    const min = Math.min(...v); const max = Math.max(...v);
    const marge = Math.max(5, Math.round((max - min) * 0.35));
    return [Math.max(-100, Math.floor((min - marge) / 5) * 5), Math.min(100, Math.ceil((max + marge) / 5) * 5)];
  }, [serie]);

  if (erreur) return <div className="status-error sat-bandeau">{erreur}</div>;
  if (!data) return <div className="sat-vide">Chargement…</div>;

  const t = data.total;
  const p = data.precedent;
  const rep = data.repartition || [];
  const maxRep = Math.max(1, ...rep.map((r) => r.n));
  const totalRep = rep.reduce((a, r) => a + r.n, 0);
  const visibles = toutVoir ? sites : sites.slice(0, 10);

  return (
    <>
      <div className="page-toolbar">
        <select className={`page-select${formulaireId ? ' active-filter' : ''}`} value={formulaireId}
          onChange={(e) => setFormulaireId(e.target.value)}>
          <option value="">Tous les formulaires</option>
          {formulaires.map((f) => <option key={f.id} value={f.id}>{f.nom} — {f.concession_nom}</option>)}
        </select>
        <div className="sat-periodes">
          {RACCOURCIS.map((r) => (
            <button key={r.cle} type="button"
              className={`filter-chip ${raccourci === r.cle ? 'chip-active' : 'chip-default'}`}
              onClick={() => choisirRaccourci(r)}>{r.label}</button>
          ))}
        </div>
        <label className="sat-periode-champ">du <input type="date" className="page-select" value={du}
          onChange={(e) => { setDu(e.target.value); setRaccourci(null); }} /></label>
        <label className="sat-periode-champ">au <input type="date" className="page-select" value={auj}
          onChange={(e) => { setAuj(e.target.value); setRaccourci(null); }} /></label>
      </div>

      {/* Un KPI dominant, trois d'appui : l'œil doit savoir où se poser. */}
      <div className="sat-kpis">
        <div className="sat-kpi sat-kpi-hero">
          <div className="sat-kpi-lab">Score NPS</div>
          <div className={`sat-kpi-val sat-note-${tonScore(t.score_nps)}`}>{t.score_nps ?? '—'}</div>
          <div className="sat-kpi-sub">
            {nf.format(t.promoteurs)} promoteurs − {nf.format(t.detracteurs)} détracteurs
            {p && <> &nbsp;<Delta valeur={t.score_nps} avant={p.score_nps} /> vs période précédente ({p.score_nps ?? '—'})</>}
          </div>
        </div>
        <div className="sat-kpi">
          <div className="sat-kpi-lab">Réponses</div>
          <div className="sat-kpi-val">{nf.format(t.reponses)}</div>
          <div className="sat-kpi-sub">
            {pct(t.reponses, t.envois)} % des {nf.format(t.envois)} envoyés
            {p && <> &nbsp;<Delta valeur={t.reponses} avant={p.reponses} /></>}
          </div>
        </div>
        <div className="sat-kpi">
          <div className="sat-kpi-lab">Note moyenne</div>
          <div className="sat-kpi-val">{un(t.nps_moyen)}<small> / 10</small></div>
          <div className="sat-kpi-sub">
            {un(t.note_moyenne)} / 5 sur la livraison
            {p && <> &nbsp;<Delta valeur={t.nps_moyen} avant={p.nps_moyen} decimales={1} /></>}
          </div>
        </div>
        {/* Le seul chiffre de ce bandeau sur lequel on AGIT : un client
            mécontent qu'on n'a pas encore rappelé. « À traiter » tout court y
            mêlait les promoteurs, qu'on ne rappelle pas. */}
        <div className="sat-kpi">
          <div className="sat-kpi-lab">Détracteurs à rappeler</div>
          <div className={`sat-kpi-val${t.detracteurs_a_traiter ? ' sat-note-mauvais' : ''}`}>
            {nf.format(t.detracteurs_a_traiter)}
          </div>
          <div className="sat-kpi-sub">
            sur {nf.format(t.detracteurs)} détracteurs · {nf.format(t.a_traiter)} réponses non traitées
          </div>
          <div className="sat-kpi-bar">
            <i className="sat-fond-mauvais" style={{ width: `${pct(t.detracteurs, t.reponses) || 0}%` }} />
          </div>
        </div>
      </div>

      <div className="sat-colonnes">
        <div className="sat-carte">
          <h3>Évolution mensuelle</h3>
          <div className="sat-carte-sous">
            Score NPS par mois (courbe) et volume de réponses (barres)
          </div>
          <div className="sat-graphe">
            {serie.length > 0 ? (
              <ResponsiveContainer width="100%" height={230}>
                <ComposedChart data={serie} margin={{ top: 22, right: 8, left: -14, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" vertical={false} />
                  <XAxis dataKey="label" stroke="var(--text-muted)" fontSize={11} tickLine={false} />
                  <YAxis yAxisId="vol" hide />
                  <YAxis yAxisId="score" domain={domaineScore} stroke="var(--text-muted)" fontSize={11}
                    tickLine={false} axisLine={false} width={38} />
                  <Tooltip
                    formatter={(v, n) => (n === 'Score NPS' ? [v, n] : [nf.format(v), 'Réponses'])}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border-default)' }}
                  />
                  <Bar yAxisId="vol" dataKey="reponses" fill="var(--color-info-light)" radius={[3, 3, 0, 0]} name="Réponses" />
                  <Line yAxisId="score" type="monotone" dataKey="score_nps" name="Score NPS"
                    stroke="var(--color-accent)" strokeWidth={2.5} dot={{ r: 3.5, fill: '#fff', strokeWidth: 2 }}>
                    <LabelList dataKey="score_nps" position="top" offset={9}
                      style={{ fontSize: 10.5, fontWeight: 700, fill: 'var(--text-primary)' }} />
                  </Line>
                </ComposedChart>
              </ResponsiveContainer>
            ) : <div className="sat-vide">Aucune réponse sur la période.</div>}
          </div>
        </div>

        <div className="sat-carte">
          <h3>Répartition des notes</h3>
          <div className="sat-carte-sous">Sur les {nf.format(totalRep)} réponses notées — la moyenne cache la forme</div>
          <div className="sat-rep">
            {Array.from({ length: 11 }, (_, n) => 10 - n).map((n) => {
              const v = rep.find((r) => r.note === n)?.n || 0;
              const ton = n >= 9 ? 'bon' : 'mauvais';
              return (
                <div className="sat-rep-l" key={n}>
                  <span className="sat-rep-n">{n}</span>
                  <span className="sat-rep-t"><i className={`sat-fond-${ton}`} style={{ width: `${(v / maxRep) * 100}%` }} /></span>
                  <span className="sat-rep-v">{v ? nf.format(v) : ''}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="sat-carte">
        <h3>Tous les points de vente</h3>
        <div className="sat-carte-sous">
          {sites.length} points de vente · cliquez un en-tête pour trier
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th className="sat-tri" onClick={() => trier('site_nom')}>Point de vente{fleche('site_nom')}</th>
                <th className="num sat-tri" onClick={() => trier('envois')}>Envoyés{fleche('envois')}</th>
                <th className="num sat-tri" onClick={() => trier('reponses')}>Réponses{fleche('reponses')}</th>
                <th className="num sat-tri" onClick={() => trier('taux')}>Taux{fleche('taux')}</th>
                <th className="num sat-tri" onClick={() => trier('score_nps')}>Score NPS{fleche('score_nps')}</th>
                <th className="num sat-tri" onClick={() => trier('note_moyenne')}>Note /5{fleche('note_moyenne')}</th>
                <th className="num sat-tri" onClick={() => trier('a_traiter')}>À rappeler{fleche('a_traiter')}</th>
                <th className="sat-col-rep">Répartition</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((s) => (
                <tr key={`${s.site_code}-${s.site_nom}`}>
                  <td>{s.site_nom} <span className="sat-vide-inline">{s.site_code}</span></td>
                  <td className="num muted">{nf.format(s.envois)}</td>
                  <td className="num strong">{nf.format(s.reponses)}</td>
                  <td className="num muted">{pct(s.reponses, s.envois)} %</td>
                  <td className="num"><span className={`sat-note sat-note-${tonScore(s.score_nps)}`}>{s.score_nps ?? '—'}</span></td>
                  <td className="num muted">{un(s.note_moyenne)}</td>
                  <td className={`num${s.detracteurs_a_traiter > 0 ? ' alert' : ''}`}
                    title={`${s.a_traiter} réponse(s) non traitée(s) au total`}>
                    {nf.format(s.detracteurs_a_traiter)}
                  </td>
                  <td className="sat-col-rep">
                    <div className="sat-bar" title={`${s.promoteurs} promoteurs · ${s.detracteurs} détracteurs`}>
                      {s.promoteurs > 0 && <div className="sat-fond-bon" style={{ flexGrow: s.promoteurs }} />}
                      {s.detracteurs > 0 && <div className="sat-fond-mauvais" style={{ flexGrow: s.detracteurs }} />}
                    </div>
                  </td>
                </tr>
              ))}
              {sites.length === 0 && (
                <tr><td colSpan={8} className="sat-vide">Aucune réponse sur cette période.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {sites.length > 10 && (
          <button className="btn-secondary sat-voir-tout" onClick={() => setToutVoir((v) => !v)}>
            {toutVoir ? 'Ne montrer que les 10 premiers' : `Voir les ${sites.length} points de vente`}
          </button>
        )}
      </div>

      <p className="sat-aide">
        Le <b>score NPS</b> est la part de promoteurs (9-10) moins celle de détracteurs (0-8), en points —
        à ne pas confondre avec la note moyenne sur 10. La période porte sur la <b>date de réponse</b> :
        un questionnaire envoyé fin août et rempli en septembre est un retour de septembre.
      </p>
    </>
  );
}
